import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

/**
 * Navigation state service for passing data between routes without exposing IDs in URLs
 *
 * This service stores navigation context (like document IDs) in sessionStorage, allowing
 * clean URLs while still passing necessary data to components. Using sessionStorage
 * ensures state survives page refresh but is cleared when the browser tab is closed.
 *
 * Combined with the navigation access guard, this provides:
 * - Clean URLs without exposed IDs
 * - Protection against URL sharing/copying (state is session-specific)
 * - Support for page refresh (state persists in sessionStorage)
 * - Backend authorization is still the primary security layer
 *
 * Usage:
 * ```typescript
 * // When navigating:
 * this.navigationState.navigateTo('/carrier/booking-management/additional-info', {
 *   freightOrderId: '123',
 *   bookingGroupId: 'bg-123',
 *   deliveryId: '456'
 * });
 *
 * // In destination component:
 * const state = this.navigationState.getState<AdditionalInfoState>();
 * if (!state) {
 *   this.router.navigate(['/404']);
 *   return;
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class NavigationStateService {
  private static readonly STORAGE_KEY = 'nav_state';
  private static readonly STATE_KEY_STORAGE = 'nav_state_key';

  private state: Record<string, unknown> | null = null;
  private stateKey: string | null = null;

  constructor(private router: Router) {
    // Restore state from sessionStorage on service initialization (for page refresh)
    this.restoreFromStorage();
  }

  /**
   * Restore state from sessionStorage (for page refresh support)
   */
  private restoreFromStorage(): void {
    try {
      const storedState = sessionStorage.getItem(NavigationStateService.STORAGE_KEY);
      const storedKey = sessionStorage.getItem(NavigationStateService.STATE_KEY_STORAGE);

      if (storedState) {
        this.state = JSON.parse(storedState);
        this.stateKey = storedKey;
      }
    } catch (error) {
      console.warn('Failed to restore navigation state from sessionStorage:', error);
      this.state = null;
      this.stateKey = null;
    }
  }

  /**
   * Save state to sessionStorage (for page refresh support)
   */
  private saveToStorage(): void {
    try {
      if (this.state) {
        sessionStorage.setItem(NavigationStateService.STORAGE_KEY, JSON.stringify(this.state));
        if (this.stateKey) {
          sessionStorage.setItem(NavigationStateService.STATE_KEY_STORAGE, this.stateKey);
        }
      } else {
        sessionStorage.removeItem(NavigationStateService.STORAGE_KEY);
        sessionStorage.removeItem(NavigationStateService.STATE_KEY_STORAGE);
      }
    } catch (error) {
      console.warn('Failed to save navigation state to sessionStorage:', error);
    }
  }

  /**
   * Navigate to a route with state data (IDs hidden from URL)
   *
   * @param route - The route path as string or string array
   * @param state - The state data to pass to the destination component
   * @param options - Optional navigation options
   * @param options.stateKey - Custom key for identifying this state
   * @param options.relativeTo - ActivatedRoute for relative navigation
   */
  navigateTo<T extends Record<string, unknown>>(
    route: string | string[],
    state: T,
    options?: { stateKey?: string; relativeTo?: ActivatedRoute }
  ): Promise<boolean> {
    this.state = state;
    this.stateKey = options?.stateKey || (Array.isArray(route) ? route.join('/') : route);

    // Save to sessionStorage for refresh support
    this.saveToStorage();

    const routeArray = Array.isArray(route) ? route : [route];
    return this.router.navigate(routeArray, {
      state: { __navState: true }, // Marker to indicate state-based navigation
      relativeTo: options?.relativeTo,
    });
  }

  /**
   * Get the current navigation state
   * Returns null if no state is available (direct access attempt)
   */
  getState<T extends Record<string, unknown>>(): T | null {
    return this.state as T | null;
  }

  /**
   * Get a specific value from the state
   */
  getValue<T>(key: string): T | undefined {
    return this.state?.[key] as T | undefined;
  }

  /**
   * Check if navigation state exists
   */
  hasState(): boolean {
    return this.state !== null;
  }

  /**
   * Clear the navigation state (called after component uses it or on navigation away)
   */
  clearState(): void {
    this.state = null;
    this.stateKey = null;
    // Also clear from sessionStorage
    this.saveToStorage();
  }

  /**
   * Check if current state matches a specific key
   */
  isStateFor(key: string): boolean {
    return this.stateKey === key;
  }
}

/**
 * Interface for additional info navigation state
 */
export interface AdditionalInfoState {
  freightOrderId: string;
  bookingGroupId: string;
  deliveryId?: string;
  [key: string]: unknown;
}

/**
 * Interface for book slot navigation state
 */
export interface BookSlotState {
  freightOrderId: string;
  bookingGroupId?: string;
  [key: string]: unknown;
}

/**
 * Interface for detail view navigation state
 */
export interface DetailViewState {
  id: string;
  type?: string;
  [key: string]: unknown;
}

/**
 * Interface for expedition check-in/check-out/security-check/vehicle-check navigation state
 */
export interface ExpeditionCheckState {
  freightOrderId: string;
  /** Booking group ID - required for loading carrier-uploaded pictures in check-in/out forms */
  bookingGroupId?: string;
  /** When true, the form is displayed in read-only mode (view only) */
  readonly?: boolean;
  [key: string]: unknown;
}

/**
 * Interface for operating loading dialog navigation state
 */
export interface LoadingDialogState {
  freightOrderId: string;
  bookingGroupId: string;
  freightOrderItem?: unknown;
  [key: string]: unknown;
}

/**
 * Interface for edit user navigation state
 */
export interface EditUserState {
  userId: string;
  [key: string]: unknown;
}

/**
 * Interface for form configuration navigation state
 */
export interface FormConfigState {
  formCode: string;
  [key: string]: unknown;
}
